import React, { useState, useEffect } from "react";
import {
    Drawer,
    Form,
    Input,
    Select,
    Button,
    message,
    Spin,
    Card,
    Descriptions,
    Tag,
    Badge,
    Typography,
    DatePicker,
} from "antd";
import dayjs from "dayjs";
import API from "../../API/API";
import {
    UserOutlined,
    CalendarOutlined,
    MailOutlined,
    PhoneOutlined,
    CarOutlined,
    ToolOutlined
} from '@ant-design/icons';

const { Title } = Typography;

const { Option } = Select;

const ISSUE_TYPES = [
  "Defect",
  "Missing Part",
  "Wrong Part",
  "Damage",
  "Warranty",
  "Recall",
  "Shipping Lost",
  "Cx placed incorrect order",
  "Corrosion / Rusty",
  "Miles / Tag",
  "Yard shipped incorrect part",
  "Cx provided Incorrect Spec",
  "Part does not fit",
  "Yard Issues",
  "Wiring harnesses & Plug in Switch",
  "Customer Refused the Package",
  "Other"
];
const ISSUE_STATUS = [
  "Open",
  "In Progress",
  "Waiting for Vendor",
  "Resolved",
  "Closed",
  "Replacement",
  "Refund to cx",
  "Refund from the Yard",
  "Part in Transit",
  "Charge back",
  "Dispute"
];
const PRIORITY_LEVELS = ["Low", "Medium", "High", "Critical"];
const REPORTED_BY = ["Customer", "Internal", "Dealer","Yard"];
const formatPSTDateWithDay = (dateString) => {
    if (!dateString) return { date: "-", day: "-", daysAgo: "-" };

    const date = new Date(dateString);

    // PST formatted date + day
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "2-digit"
    });

    const parts = formatter.formatToParts(date);

    const day = parts.find(p => p.type === "weekday")?.value;
    const formattedDate = parts
        .filter(p => ["month", "day", "year"].includes(p.type))
        .map(p => p.value)
        .join(" ");

    // Calculate days ago (PST)
    const now = new Date();
    const pstNow = new Date(
        now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
    );
    pstNow.setHours(0, 0, 0, 0);

    const pstOrderDate = new Date(
        date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" })
    );
    pstOrderDate.setHours(0, 0, 0, 0);

    const diffDays = Math.floor(
        (pstNow - pstOrderDate) / (1000 * 60 * 60 * 24)
    );

    return {
        day,
        date: formattedDate,
        daysAgo:
            diffDays === 0
                ? "Today"
                : `${diffDays} day${diffDays > 1 ? "s" : ""} ago`
    };
};

// Shared two-line rendering for a lead search result: bold name + order number
// on top, muted smaller email + phone below. Used for both the open dropdown
// list and the closed selection box (via Select's optionLabelProp) so they
// always look identical.
const renderLeadOptionLabel = (lead) => (
    <div style={{ padding: "2px 0", lineHeight: 1.4 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1f1f1f" }}>
            {lead.personalInfo?.fullName || "Unnamed"}
            {lead.orderNumber && (
                <span style={{ fontWeight: 500, color: "#595959" }}> — {lead.orderNumber}</span>
            )}
        </div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 1 }}>
            {lead.personalInfo?.email || "No email"} &nbsp;·&nbsp; {lead.personalInfo?.phoneNumber || "No phone"}
        </div>
    </div>
);

const AddIssueDrawer = ({ visible, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [leads, setLeads] = useState([]);
    const [fetchingLeads, setFetchingLeads] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [leadSearchTerm, setLeadSearchTerm] = useState("");
    const [form] = Form.useForm();
    const [currentUser, setCurrentUser] = useState(null);
const [serviceAgentDetails, setServiceAgentDetails] = useState(null);
const [loadingServiceAgent, setLoadingServiceAgent] = useState(false);

useEffect(() => {
    const fetchServiceAgent = async () => {
        if (selectedLead?.serviceHandledBy && typeof selectedLead.serviceHandledBy === 'string') {
            setLoadingServiceAgent(true);
            try {
                const response = await API.GetUserById(selectedLead.serviceHandledBy);
                if (response?.success && response?.data) {
                    setServiceAgentDetails(response.data);
                }
            } catch (error) {
                console.error('Error fetching service agent:', error);
            } finally {
                setLoadingServiceAgent(false);
            }
        } else {
            setServiceAgentDetails(selectedLead?.serviceHandledBy || null);
        }
    };
    
    if (selectedLead) {
        fetchServiceAgent();
    }
}, [selectedLead]);


    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("userData"));
        if (userData) {
            setCurrentUser(userData);
        }
    }, []);
    // Search SOLD leads by name / order # / email / phone (backend already
    // matches all four via getAll's `search` param — see ci-crm-backend
    // LeadController.js getAll's search branch).
    const MIN_SEARCH_LEN = 2;
    const LEAD_SEARCH_LIMIT = 20;

    const fetchLeads = async (search = "") => {
        if (search.trim().length > 0 && search.trim().length < MIN_SEARCH_LEN) {
            setLeads([]);
            return;
        }
        setFetchingLeads(true);
        try {
            const response = await API.leadGetAll({
                status: "Sales Made",
                search: search.trim() || undefined,
                // Lead picker: name, email, phone, ZIP, order # and VIN are
                // enough to find a lead, and much faster than every lead field.
                searchFields: 'quick',
                // Only the fields the picker shows (LeadController
                // LEAD_VIEW_FIELDS['issue-lead-picker']).
                view: 'issue-lead-picker',
                page: 1,
                limit: LEAD_SEARCH_LIMIT,
            });

            setLeads(response?.status && response.data?.length ? response.data : []);
        } catch (error) {
            console.error("Failed to fetch leads:", error);
            message.error("Error fetching leads");
            setLeads([]);
        } finally {
            setFetchingLeads(false);
        }
    };

    // Reset on open
    useEffect(() => {
        if (visible) {
            form.resetFields();
            form.setFieldsValue({ issue_created_date: dayjs() });
            setSelectedLead(null);
            setLeadSearchTerm("");
            setLeads([]);
        }
    }, [visible]);

    // Debounce the lead search so it doesn't fire a request per keystroke
    useEffect(() => {
        if (!visible) return;
        const handle = setTimeout(() => {
            fetchLeads(leadSearchTerm);
        }, 350);
        return () => clearTimeout(handle);
    }, [leadSearchTerm, visible]);

    // Lead select handler
    const handleLeadChange = (leadId) => {
        const lead = leads.find((l) => l._id === leadId);
        setSelectedLead(lead || null);
    };

    // Submit
    const handleSubmit = async (values) => {
        if (!currentUser) {
            message.error("User not authenticated");
            return;
        }

        // ✅ Add lead validation here
        if (!values.lead) {
            message.error("Please select a lead");
            return;
        }

        setLoading(true);
        try {
            const submitValues = { ...values };
            if (submitValues.issueType === "Other") {
                submitValues.customIssueType = submitValues.customIssueType?.trim() || "";
            } else {
                delete submitValues.customIssueType;
            }

            // The issue form has no file uploads — send plain JSON so the backend's
            // express.json() body parser actually receives the fields.
            const payload = {
                ...submitValues,
                // The picker hands back a dayjs object; the API needs an instant.
                issue_created_date: submitValues.issue_created_date
                    ? submitValues.issue_created_date.toISOString()
                    : undefined,
                createdBy: currentUser.userName,
            };

            const res = await API.createIssue(payload);

            if (res?.success) {
                message.success("Issue created successfully");
                form.resetFields();
                setSelectedLead(null);
                onSuccess?.(res.data); // hand the created issue back for an instant list update
                onClose();
            } else {
                message.error(res?.message || "Failed to create issue");
            }
        } catch (error) {
            console.error("Create issue error:", error);
            message.error(error.response?.data?.message || "Error creating issue");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer
            title="Create New Issue"
            width={600}
            onClose={onClose}
            open={visible}
            destroyOnClose
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                {/* Lead */}
                <Form.Item
                    label="Lead"
                    name="lead"
                    rules={[{ required: true, message: "Please select a lead" }]}
                >
                    <Select
                        className="lead-search-select"
                        popupClassName="lead-search-select-dropdown"
                        placeholder="Search sold lead by name, order #, email, or phone"
                        showSearch
                        allowClear
                        virtual={false}
                        filterOption={false}
                        optionLabelProp="label"
                        onSearch={setLeadSearchTerm}
                        loading={fetchingLeads}
                        onChange={handleLeadChange}
                        notFoundContent={
                            fetchingLeads ? (
                                <Spin size="small" />
                            ) : leadSearchTerm.trim().length > 0 && leadSearchTerm.trim().length < MIN_SEARCH_LEN ? (
                                "Type at least 2 characters"
                            ) : leadSearchTerm.trim().length === 0 ? (
                                "Type a name, order #, email, or phone to search"
                            ) : (
                                "No sold leads found"
                            )
                        }
                    >
                        {(leads || []).map((lead) => (
                            <Option key={lead._id} value={lead._id} label={renderLeadOptionLabel(lead)}>
                                {renderLeadOptionLabel(lead)}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Selected Lead Details */}
              {selectedLead && (
    <Card
        size="small"
        style={{
            marginBottom: 16,
            borderRadius: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #d9d9d9'
        }}
    >
        {/* Header Section */}
        <div style={{
            backgroundColor: '#1890ff',
            padding: '12px 16px',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            margin: '-1px -1px 16px -1px'
        }}>
            <div style={{
                color: 'white',
                fontSize: '16px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <UserOutlined style={{ fontSize: '16px' }} />
                Lead Information
            </div>
        </div>

        {/* Content Section */}
        <div style={{ padding: '0 16px 16px 16px' }}>
            {/* Customer Name */}
            <div style={{
                marginBottom: '12px',
                padding: '8px',
                backgroundColor: '#e6f7ff',
                borderRadius: '6px'
            }}>
                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#1890ff' }}>
                    {selectedLead.personalInfo?.fullName || 'N/A'}
                </div>
                <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    Customer
                </div>
            </div>

            {/* Order Number */}
            {selectedLead.orderNumber && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                    padding: '8px',
                    backgroundColor: '#f6ffed',
                    borderRadius: '6px',
                    border: '1px solid #b7eb8f'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Badge status="success" />
                        <span style={{ color: '#595959' }}>Order Number:</span>
                    </div>
                    <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                        {selectedLead.orderNumber}
                    </div>
                </div>
            )}

            {/* Order Date */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalendarOutlined style={{ color: '#52c41a' }} />
                    <span style={{ color: '#595959' }}>Order Date:</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                    {(() => {
                        const { day, date, daysAgo } =
                            formatPSTDateWithDay(selectedLead.orderDate);

                        return (
                            <>
                                <div style={{ fontWeight: 500 }}>
                                    {day}, {date}
                                </div>
                                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                    {daysAgo}
                                </div>
                            </>
                        );
                    })()}
                </div>
            </div>

          {/* AGENT SECTION - Sales Agent */}
<div style={{
    marginBottom: '12px',
    padding: '12px',
    backgroundColor: '#f9f0ff',
    borderRadius: '6px',
    border: '1px solid #d3adf7'
}}>
    <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px'
    }}>
        <UserOutlined style={{ color: '#722ed1' }} />
        <span style={{ fontWeight: 500, color: '#531dab' }}>Sales Agent</span>
    </div>
    {selectedLead.handledBy ? (
        <div>
            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                {/* Check if handledBy is populated object or just ID */}
                {typeof selectedLead.handledBy === 'object' && selectedLead.handledBy.firstName 
                    ? `${selectedLead.handledBy.firstName} ${selectedLead.handledBy.lastName || ''}`.trim()
                    : selectedLead.handledBy.userName || selectedLead.agentName || 'Sales Agent'}
            </div>
            {typeof selectedLead.handledBy === 'object' && selectedLead.handledBy.email && (
                <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '4px' }}>
                    <MailOutlined style={{ marginRight: '4px' }} />
                    {selectedLead.handledBy.email}
                </div>
            )}
            {typeof selectedLead.handledBy === 'object' && selectedLead.handledBy.directNumber && (
                <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>
                    <PhoneOutlined style={{ marginRight: '4px' }} />
                    {selectedLead.handledBy.directNumber}
                </div>
            )}
        </div>
    ) : (
        <div style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
            No sales agent assigned
        </div>
    )}
</div>

        {/* SERVICE AGENT SECTION */}
<div style={{
    marginBottom: '12px',
    padding: '12px',
    backgroundColor: '#f6ffed',
    borderRadius: '6px',
    border: '1px solid #b7eb8f'
}}>
    <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px'
    }}>
        <ToolOutlined style={{ color: '#52c41a' }} />
        <span style={{ fontWeight: 500, color: '#389e0d' }}>Service Agent</span>
    </div>
    
    {/* Check if service agent exists */}
    {(serviceAgentDetails || selectedLead.serviceHandledBy) ? (
        <div>
            {/* Display name from fetched details or directly from lead */}
            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                {serviceAgentDetails ? (
                    // Use fetched details
                    `${serviceAgentDetails.firstName || ''} ${serviceAgentDetails.lastName || ''}`.trim() || 
                    serviceAgentDetails.userName || 'Service Agent'
                ) : (
                    // Fallback to lead data if already populated
                    selectedLead.serviceHandledBy?.firstName && selectedLead.serviceHandledBy?.lastName 
                        ? `${selectedLead.serviceHandledBy.firstName} ${selectedLead.serviceHandledBy.lastName}`
                        : selectedLead.serviceHandledBy?.userName || selectedLead.agentName || 'Service Agent'
                )}
            </div>
            
          
            
          
            
            {/* Show loading indicator while fetching */}
            {selectedLead.serviceHandledBy && typeof selectedLead.serviceHandledBy === 'string' && !serviceAgentDetails && (
                <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '4px' }}>
                    <Spin size="small" /> Loading service agent details...
                </div>
            )}
        </div>
    ) : (
        <div style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
            No service agent assigned
        </div>
    )}
</div>
{loadingServiceAgent && (
    <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '4px' }}>
        <Spin size="small" /> Loading service agent details...
    </div>
)}
            {/* Contact Info Row - Phone & Email */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '16px'
            }}>
                {/* Phone */}
                <div style={{
                    padding: '12px',
                    backgroundColor: '#fafafa',
                    borderRadius: '6px',
                    border: '1px solid #f0f0f0'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '6px'
                    }}>
                        <PhoneOutlined style={{ color: '#1890ff' }} />
                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>Phone</span>
                    </div>
                    <a href={`tel:${selectedLead.personalInfo?.phoneNumber}`} style={{
                        fontWeight: 500,
                        color: '#262626',
                        textDecoration: 'none'
                    }}>
                        {selectedLead.personalInfo?.phoneNumber || 'N/A'}
                    </a>
                </div>

                {/* Email */}
                <div style={{
                    padding: '12px',
                    backgroundColor: '#fafafa',
                    borderRadius: '6px',
                    border: '1px solid #f0f0f0'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '6px'
                    }}>
                        <MailOutlined style={{ color: '#1890ff' }} />
                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>Email</span>
                    </div>
                    <a href={`mailto:${selectedLead.personalInfo?.email}`} style={{
                        fontWeight: 500,
                        color: '#262626',
                        textDecoration: 'none',
                        wordBreak: 'break-all'
                    }}>
                        {selectedLead.personalInfo?.email || 'N/A'}
                    </a>
                </div>
            </div>

            {/* Vehicle Info Card */}
            <div style={{
                marginBottom: '16px',
                border: '1px solid #f0f0f0',
                borderRadius: '6px',
                overflow: 'hidden'
            }}>
                <div style={{
                    backgroundColor: '#f6ffed',
                    padding: '10px 12px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CarOutlined style={{ color: '#52c41a' }} />
                        <span style={{ fontWeight: 500 }}>Vehicle</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                            fontSize: '12px',
                            color: '#8c8c8c',
                            backgroundColor: '#fff',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            border: '1px solid #d9d9d9'
                        }}>
                            {selectedLead.vehicleInfo?.year || 'N/A'}
                        </span>
                    </div>
                </div>

                <div style={{ padding: '12px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '8px'
                    }}>
                        <div>
                            <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '4px' }}>
                                {selectedLead.vehicleInfo?.make || 'N/A'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                Make
                            </div>
                        </div>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>
                                {selectedLead.vehicleInfo?.model || 'N/A'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                Model
                            </div>
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingTop: '8px',
                        borderTop: '1px dashed #f0f0f0'
                    }}>
                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>VIN Number:</span>
                        <Tag color="default" style={{ fontSize: '12px' }}>
                            {selectedLead.vehicleInfo?.vin || 'N/A'}
                        </Tag>
                    </div>
                </div>
            </div>

            {/* Part Info */}
            <div style={{
                backgroundColor: '#fff7e6',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #ffd591'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ToolOutlined style={{ color: '#fa8c16' }} />
                        <div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '2px' }}>
                                Requested Part
                            </div>
                            <div style={{ fontWeight: 600, fontSize: '16px' }}>
                                {selectedLead.vehicleInfo?.part || 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Card>
)}

                {/* Issue Type */}
                <Form.Item
                    label="Issue Type"
                    name="issueType"
                    rules={[{ required: true, message: "Please select issue type" }]}
                >
                    <Select
                        placeholder="Select issue type"
                        onChange={(value) => {
                            if (value !== "Other") {
                                form.setFieldsValue({ customIssueType: undefined });
                            }
                        }}
                    >
                        {ISSUE_TYPES.map((type) => (
                            <Option key={type} value={type}>
                                {type}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                        prevValues.issueType !== currentValues.issueType
                    }
                >
                    {({ getFieldValue }) =>
                        getFieldValue("issueType") === "Other" && (
                            <Form.Item
                                label="Custom Issue Type"
                                name="customIssueType"
                                preserve={false}
                                rules={[
                                    { required: true, message: "Please enter custom issue type" },
                                    {
                                        validator: (_, value) =>
                                            value && value.trim()
                                                ? Promise.resolve()
                                                : Promise.reject(new Error("Please enter custom issue type"))
                                    }
                                ]}
                            >
                                <Input placeholder="Enter custom issue type" />
                            </Form.Item>
                        )
                    }
                </Form.Item>

                {/* Priority */}
                <Form.Item
                    label="Priority"
                    name="priority"
                    rules={[{ required: true, message: "Please select priority" }]}
                >
                    <Select placeholder="Select priority">
                        {PRIORITY_LEVELS.map((level) => (
                            <Option key={level} value={level}>
                                {level}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Status */}
                <Form.Item
                    label="Status"
                    name="status"
                    rules={[{ required: true, message: "Please select status" }]}
                >
                    <Select placeholder="Select status">
                        {ISSUE_STATUS.map((status) => (
                            <Option key={status} value={status}>
                                {status}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Reported By */}
                <Form.Item
                    label="Reported By"
                    name="reportedBy"
                    rules={[{ required: true, message: "Please select reported by" }]}
                >
                    <Select placeholder="Select reported by">
                        {REPORTED_BY.map((r) => (
                            <Option key={r} value={r}>
                                {r}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Issue Created Date */}
                <Form.Item
                    label="Issue Created Date"
                    name="issue_created_date"
                    tooltip="The date the issue was raised. Defaults to now."
                    rules={[{ required: true, message: "Please select the issue created date" }]}
                >
                    <DatePicker
                        showTime={{ format: "HH:mm" }}
                        format="YYYY-MM-DD HH:mm"
                        placeholder="Select issue created date"
                        style={{ width: "100%" }}
                    />
                </Form.Item>

                {/* Customer Description */}
                <Form.Item
                    label="Customer Description"
                    name="description"
                    rules={[{ required: true, message: "Please enter customer description" }]}
                >
                    <Input.TextArea rows={4} placeholder="Enter customer description" />
                </Form.Item>

                {/* Yard Description - NEW FIELD ADDED HERE */}
                <Form.Item
                    label="Yard Description"
                    name="yardComment"
                    tooltip="Internal notes about yard communication"
                >
                    <Input.TextArea
                        rows={3}
                        placeholder="Enter any yard-related comments or notes..."
                    />
                </Form.Item>

                {/* Calculation Notes - optional */}
                <Form.Item
                    label="Calculation Notes (Optional)"
                    name="calculationNotes"
                    tooltip="Any calculation-related notes for this issue"
                >
                    <Input.TextArea
                        rows={3}
                        placeholder="Add any calculation notes here..."
                    />
                </Form.Item>

                <Button type="primary" htmlType="submit" loading={loading} block>
                    Create Issue
                </Button>
            </Form>
            <style>{`
                /* The lead picker's options are two lines tall (name+order, then
                   email/phone) — antd's Select assumes single-line content by
                   default, which clips/overlaps taller content. These overrides
                   let both the closed selector and the open dropdown grow to fit. */
                .lead-search-select .ant-select-selector {
                    height: auto !important;
                    min-height: 46px !important;
                    padding: 6px 11px !important;
                }
                .lead-search-select .ant-select-selection-search {
                    display: flex;
                    align-items: center;
                    inset-inline-start: 11px;
                    inset-inline-end: 11px;
                }
                .lead-search-select .ant-select-selection-item,
                .lead-search-select .ant-select-selection-placeholder {
                    display: flex;
                    align-items: center;
                    height: auto;
                    line-height: 1.4;
                    padding-inline-end: 18px;
                }
                .lead-search-select-dropdown .ant-select-item-option {
                    padding: 8px 12px;
                }
            `}</style>
        </Drawer>
    );
};

export default AddIssueDrawer;
